import React, { useState } from "react";
import { Card, CardContent, Grid, TextField } from "@mui/material";
import { EditableRateSet } from "../../api/rateSet";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isValid } from "date-fns";

interface RateSetEditProps {
  value: EditableRateSet;
  onChange: (newValue: EditableRateSet) => void;
}

const RateSetEdit = ({ value, onChange, ...rest }: RateSetEditProps) => {
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onChange({
      ...value,
      [event.target.name]: event.target.value,
    });
  };

  const [effectiveDate, setEffectiveDate] = useState<Date | null>(
    new Date(value.effective_date_utc)
  );
  const [expirationDate, setExpirationDate] = useState<Date | null>(
    value.expiration_date_utc
      ? new Date(value.expiration_date_utc as string)
      : null
  );

  const handleEffectiveDateChange = (changedDate: Date | null) => {
    setEffectiveDate(changedDate);
    if (changedDate && isValid(changedDate)) {
      onChange({
        ...value,
        effective_date_utc: changedDate.toISOString(),
      });
    } else {
      onChange({
        ...value,
        effective_date_utc: "",
      });
    }
  };

  const handleExpirationDateChange = (changedDate: Date | null) => {
    setExpirationDate(changedDate);
    if (changedDate && isValid(changedDate)) {
      onChange({
        ...value,
        expiration_date_utc: changedDate.toISOString(),
      });
    } else {
      onChange({
        ...value,
        expiration_date_utc: undefined,
      });
    }
  };

  return (
    <form autoComplete="off" noValidate {...rest}>
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                helperText="Please specify the name"
                label="Name"
                name="name"
                onChange={handleChange}
                required
                value={value.name}
                variant="outlined"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                helperText="Please describe the rate set"
                label="Description"
                name="description"
                onChange={handleChange}
                value={value.description}
                variant="outlined"
                multiline={true}
                rows={4}
                inputProps={{ maxLength: 256 }}
              />
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Effective Date"
                  value={effectiveDate}
                  onChange={(newValue) => {
                    handleEffectiveDateChange(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      helperText="Please specify the effective date"
                      name="effective_date_utc"
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Expiration Date"
                  value={expirationDate}
                  minDate={effectiveDate}
                  onChange={(newValue) => {
                    handleExpirationDateChange(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      helperText="Please specify the expiration date"
                      name="expiration_date_utc"
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </form>
  );
};

export default RateSetEdit;
